import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'kiosk_tabs';
const MAX_TABS = 5;

interface KioskTabsState {
  tabs: string[];
  activeTab: string | null;
  activeLinePerTab: Record<string, string | null>;
}

const empty: KioskTabsState = { tabs: [], activeTab: null, activeLinePerTab: {} };

function load(): KioskTabsState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as KioskTabsState) : empty;
  } catch {
    return empty;
  }
}

function save(state: KioskTabsState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function useKioskTabs() {
  const [state, setState] = useState<KioskTabsState>(load);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
    save(state);
  });

  const openTab = useCallback((encId: string) => {
    setState((prev) => {
      if (prev.tabs.includes(encId)) {
        return { ...prev, activeTab: encId };
      }
      let tabs = [...prev.tabs, encId];
      if (tabs.length > MAX_TABS) {
        tabs = tabs.slice(1);
      }
      return { ...prev, tabs, activeTab: encId };
    });
  }, []);

  const closeTab = useCallback((encId: string) => {
    setState((prev) => {
      const idx = prev.tabs.indexOf(encId);
      const tabs = prev.tabs.filter((t) => t !== encId);
      const newActive: string | null =
        tabs.length === 0
          ? null
          : (tabs[Math.max(0, idx - 1)] ?? null);
      return { ...prev, tabs, activeTab: newActive };
    });
  }, []);

  const activateTab = useCallback((encId: string) => {
    setState((prev) => ({ ...prev, activeTab: encId }));
  }, []);

  const setActiveLine = useCallback((tabEncId: string, lineUid: string | null) => {
    setState((prev) => ({
      ...prev,
      activeLinePerTab: { ...prev.activeLinePerTab, [tabEncId]: lineUid },
    }));
  }, []);

  return {
    tabs: state.tabs,
    activeTab: state.activeTab,
    activeLinePerTab: state.activeLinePerTab,
    openTab,
    closeTab,
    activateTab,
    setActiveLine,
  };
}
